import type { McapCanonicalRepresentation } from '@/features/assets/types/asset';
import type { SegmentPlan } from '@/features/mcap/types/contracts';

export interface VideoTrackCatalogEntry {
  channelId: number;
}

export interface SegmentPreviewContext {
  sourceAssetId: string;
  logicalStartNs: string;
  logicalEndNs: string;
  canonicalRepresentation: McapCanonicalRepresentation;
}

/** A plan is usable only when both sides are unique and contain exactly the same channel ids. */
export function planMatchesVideoTrackCatalog(
  plan: SegmentPlan,
  catalog: ReadonlyArray<VideoTrackCatalogEntry>,
): boolean {
  const planChannels = new Set(plan.videoTracks.map((track) => track.channelId));
  const catalogChannels = new Set(catalog.map((track) => track.channelId));
  if (
    planChannels.size !== plan.videoTracks.length ||
    catalogChannels.size !== catalog.length ||
    planChannels.size !== catalogChannels.size
  ) {
    return false;
  }
  return Array.from(planChannels).every((channelId) => catalogChannels.has(channelId));
}

/** A preview may be confirmed only when the server froze the exact request and current canonical MCAP. */
export function planMatchesPreviewContext(
  plan: SegmentPlan,
  context: SegmentPreviewContext,
): boolean {
  const canonical = context.canonicalRepresentation;
  return (
    plan.sourceAssetId === context.sourceAssetId &&
    plan.logicalStartNs === context.logicalStartNs &&
    plan.logicalEndNs === context.logicalEndNs &&
    plan.canonicalObject === canonical.canonicalObject &&
    plan.canonicalBlake3 === canonical.canonicalBlake3 &&
    plan.canonicalFileSizeBytes === canonical.canonicalFileSizeBytes &&
    plan.normalizerVersion === canonical.normalizerVersion &&
    plan.layoutProfile === canonical.layoutProfile
  );
}
